import "./menu.css";
import { lang, setLang, t, type Lang } from "../i18n/index.js";
import { renderDonationFooter } from "./donations.js";
import { editNickname, loadNickname, onNicknameChange } from "../online/nickname.js";
import { HUB_ICON_HISTORY, HUB_ICON_LOCAL, HUB_ICON_SALAS, HUB_ICON_WORLD } from "./hubIcons.js";
import { BALL_MENU_SVG } from "./ballMark.js";
import { bindMenuAmbience, MENU_BACKDROP_HTML } from "./menuAmbience.js";
import { currentControls, keyLabel, moveKeys, onControlsChange, primaryKey, primeKeyLabels } from "../input/bindings.js";
import { openControlsDialog } from "./controlsDialog.js";
import { openTouchEditor } from "./touchEditor.js";
import { wantsTouchControls } from "../mobile/detect.js";

/**
 * La tira "CÓMO SE JUEGA" con las teclas de ESTE jugador: salen de
 * `input/bindings.ts`, asi que si las cambias en CONTROLES, cambian aqui.
 */
function controlsStripHtml(): string {
  const kbd = (label: string): string => `<kbd>${escapeKey(label)}</kbd>`;
  const mouse = currentControls().mouseAim ? ` / ${kbd(t("Clic"))}` : "";
  return `
          <span>${moveKeys().map(kbd).join("")} ${t("moverte")}</span>
          <span>${kbd(primaryKey("kick"))}${mouse} ${t("golpear · toque pasa, mantener dispara")}</span>
          <span>${kbd(primaryKey("dash"))} ${t("sprint")}</span>
          <span>${kbd(keyLabel("Tab"))} ${t("ver estadísticas")}</span>`;
}

function escapeKey(label: string): string {
  return label.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c]!);
}

/** La jornada ya no vive aqui: se elige dentro del lobby, con el resto del online. */
/**
 * Las tres puertas del menu, y ya.
 *
 * Hubo una cuarta —"partida rapida": una fila de modos que saltaba directa al
 * campo— y se quito. Duplicaba la puerta de LOCAL con menos control (sin
 * dorsal, sin cesped, sin plan) y convertia la primera pantalla en dos menus
 * apilados compitiendo por el mismo clic.
 */
/**
 * `relang` no es un destino: es "he cambiado el idioma, reconstruye".
 *
 * Sale por el mismo sitio que las demas elecciones porque el menu esta
 * ESPERANDO una: resolver su promesa es la unica forma de devolverle el control
 * al bucle sin dejar una promesa colgada. El bucle rehace la interfaz y vuelve
 * a enseñar el menu, asi que para quien juega no es una navegacion.
 */
export type MainMenuChoice = "online" | "private" | "career" | "world" | "relang" | "again";

export interface MainMenuHandle {
  show(): Promise<MainMenuChoice>;
  hide(): void;
  /**
   * Salir del menu porque se va a otro sitio desde fuera (un aviso de "Mike
   * te invita · Unirse"): se resuelve con "again" y el bucle mira a donde ir.
   */
  leave(): void;
  dispose(): void;
}

export interface MainMenuOptions {
  /**
   * Solo lo que el boton necesita: encender y apagar.
   *
   * Pedia el `MenuMusic` entero, y eso impedia pasarle un adaptador que
   * gobierne a la vez la del menu y la del campo — que es lo que el boton dice
   * que hace ("MUSICA · ON/OFF", sin apellidos).
   */
  music?: { isEnabled(): boolean; setEnabled(on: boolean): void };
  /** Resumen de la carrera para la pastilla del menu ("3 días seguidos"). */
  careerTeaser?: () => string;
  /** Tu sitio en el mundo ("Oro · #12 del mundo"), si ya se sabe. */
  worldTeaser?: () => string;
  /** Tus amigos: cuantos hay, que tienes pendiente, y abrir el panel. */
  social?: {
    summary(): {
      online: number;
      pending: number;
      line: string;
      party: {
        mates: Array<{ nickname: string; clientId: string; status: string }>;
        leader: boolean;
        leaderId: string;
        max: number;
      } | null;
    };
    subscribe(fn: () => void): () => void;
    open(): void;
    panelOpen(): boolean;
  };
}

function musicLabel(on: boolean): string {
  return on ? t("MÚSICA · ON") : t("MÚSICA · OFF");
}

export function createMainMenu(
  root: HTMLElement = document.body,
  options: MainMenuOptions = {},
): MainMenuHandle {
  const overlay = document.createElement("div");
  overlay.id = "main-menu";
  overlay.className = "hub-overlay hub-overlay--home";
  overlay.innerHTML = `
    ${MENU_BACKDROP_HTML}

    <div class="hub-layout">
      <div class="menu-shell hub-shell">
      <header class="menu-brand hub-brand">
        <div class="hub-hero">
          <div class="hub-tango">${BALL_MENU_SVG}</div>
          <h1 class="hub-title" aria-label="FYNTA">
            <span class="hub-title-text">FYNTA</span>
            <span class="hub-title-shine" aria-hidden="true"></span>
          </h1>
        </div>
      </header>

      <div class="hub-choices" role="list">
        <button type="button" class="hub-card is-primary" data-choice="online" style="--card-i: 0">
          <span class="hub-card-rail" aria-hidden="true"></span>
          <span class="hub-card-index" aria-hidden="true">01</span>
          <span class="hub-card-icon">${HUB_ICON_SALAS}</span>
          <span class="hub-card-body">
            <span class="hub-card-head">
              <strong>${t("SALAS")}</strong>
              <span class="hub-card-kicker">Online</span>
            </span>
            <!--
              Con amigos conectados, la descripcion dice QUIEN esta: "Mike
              esta jugando". Es la razon mas fuerte para entrar, y la tarjeta
              que lleva ahi es esta.
            -->
            <span class="hub-card-desc" data-rooms-desc>${t("Multijugador online · Salas de espera · Bots en huecos vacíos")}</span>
          </span>
          <span class="hub-card-go" aria-hidden="true">›</span>
        </button>
        <button type="button" class="hub-card" data-choice="private" style="--card-i: 1">
          <span class="hub-card-rail" aria-hidden="true"></span>
          <span class="hub-card-index" aria-hidden="true">02</span>
          <span class="hub-card-icon">${HUB_ICON_LOCAL}</span>
          <span class="hub-card-body">
            <span class="hub-card-head">
              <strong>${t("PRIVADO LOCAL / IA")}</strong>
              <span class="hub-card-kicker">${t("Sin conexión")}</span>
            </span>
            <span class="hub-card-desc">${t("Partido Local · Vs. IA o 2 Jugadores · Juega a tu ritmo")}</span>
          </span>
          <span class="hub-card-go" aria-hidden="true">›</span>
        </button>
        <!--
          El historico NO es un modo de juego, y con la tarjeta y el "03" de
          los otros dos lo parecia: tres filas identicas numeradas se leen como
          tres formas de jugar. Se queda en la misma columna para que las
          flechas sigan funcionando, pero con aspecto de acceso a tu perfil:
          sin numero, sin tarjeta y separado por una linea.
        -->
        <!--
          Los dos accesos de perfil (los tuyos y los del mundo) van lado a lado:
          son hermanos, y apilados el menu no cabia en un portatil de 768.
        -->
        <div class="hub-profiles">
        <button type="button" class="hub-card hub-card--profile" data-career style="--card-i: 2">
          <span class="hub-card-icon">${HUB_ICON_HISTORY}</span>
          <span class="hub-card-body">
            <span class="hub-card-head">
              <!--
                "MIS ESTADÍSTICAS", no "HISTÓRICO" ni "CARRERA".
                Los dos anteriores se leian como un MODO DE JUEGO —uno mas al
                lado de Salas y Local— y esto no es un modo: es donde miras tus
                numeros. La palabra tiene que decir eso sola, sin que haya que
                entrar para descubrirlo.

                Lleva el "MIS" porque "ESTADÍSTICAS" a secas ya existe como
                clave de traduccion —es la pestaña de la tabla final del
                partido, que en ingles es SCOREBOARD— y el diccionario va por
                el texto en español: la misma frase no puede traducirse de dos
                maneras. Y de paso deja claro que son las TUYAS, no las del
                partido que acabas de jugar.
              -->
              <strong>${t("MIS ESTADÍSTICAS")}</strong>
              <span class="hub-card-desc" data-career-teaser></span>
            </span>
          </span>
          <span class="hub-card-go" aria-hidden="true">›</span>
        </button>
        <!--
          Estadisticas MUNDIALES: tus numeros contra los de todos. Tambien es
          un acceso a perfiles (no un modo de juego), asi que va con el mismo
          aspecto que "Mis estadisticas", justo debajo.
        -->
        <button type="button" class="hub-card hub-card--profile hub-card--world" data-choice="world" style="--card-i: 3">
          <span class="hub-card-icon">${HUB_ICON_WORLD}</span>
          <span class="hub-card-body">
            <span class="hub-card-head">
              <strong>${t("ESTADÍSTICAS MUNDIALES")}</strong>
              <span class="hub-card-desc" data-world-teaser></span>
            </span>
          </span>
          <span class="hub-card-go" aria-hidden="true">›</span>
        </button>
        </div>
      </div>

      <!--
        Aqui vivian los prompts del MENU (flechas para moverse, Enter para
        entrar). Al lado de los controles del partido eran ruido y ademas
        sacaban la pagina de la pantalla: entre explicar como navegar un menu
        de tres opciones y explicar como se juega, gana lo segundo.
      -->

      <!--
        Los controles DEL PARTIDO, no los del menu.
        No estaban en ninguna parte: se entraba a jugar sin saber que hay
        sprint, ni que golpear es un solo boton, ni que la carga decide si sale
        pase o tiro. Y acaban de cambiar, asi que ahora mismo no los sabe
        nadie. Van aqui, en el menu, donde se leen antes de necesitarlos.
      -->
      <div class="hub-controls" data-controls>
        <span class="hub-controls-label">${t("CÓMO SE JUEGA")}</span>
        <div class="hub-controls-list" data-controls-list>${controlsStripHtml()}
        </div>
      </div>

      <!--
        Y lo mismo para el que juega con el dedo.
        Los botones del partido perdieron el texto: una palabra dentro de un
        circulo de 60 px no se lee jugando. Se distinguen por COLOR, y el color
        se explica una sola vez, aqui, que es donde hay sitio.
      -->
      <div class="hub-touch" data-touch-controls>
        <span class="hub-controls-label">${t("CÓMO SE JUEGA")}</span>
        <div class="hub-controls-list">
          <span><i class="hub-dot hub-dot--stick"></i> ${t("arrastra a la izquierda para moverte")}</span>
          <span><i class="hub-dot hub-dot--kick"></i> ${t("ámbar: toque pasa, mantener dispara")}</span>
          <span><i class="hub-dot hub-dot--field"></i> ${t("o toca el campo: el balón va ahí")}</span>
          <span><i class="hub-dot hub-dot--dash"></i> ${t("azul: correr")}</span>
        </div>
      </div>

      <div class="hub-side">
        <!-- Tus amigos: quien esta y lo que tienes pendiente, a un toque. -->
        <button type="button" class="hub-side-btn hub-friends" data-friends hidden>
          <span class="hub-friends-label" data-friends-label>${t("AMIGOS")}</span>
          <!-- Con grupo, sus caras: la escuadra de Fortnite, siempre a la vista. -->
          <span class="hub-squad" data-squad hidden></span>
          <span class="hub-friends-count" data-friends-count></span>
          <i class="hub-friends-badge" data-friends-badge hidden></i>
        </button>
        <!-- Quien eres, y la forma de cambiarlo sin buscar en ningun ajuste. -->
        <button type="button" class="hub-side-btn hub-identity" data-identity aria-label="${t("Cambiar tu apodo")}">
          <span class="hub-identity-name" data-identity-name></span>
          <span aria-hidden="true">✎</span>
        </button>
        <button
          type="button"
          class="hub-side-btn hub-music"
          data-music
          aria-pressed="true"
        >${musicLabel(true)}</button>
        <!-- Cambiar las teclas: un boton pequeño, sin pagina de ajustes. -->
        <button type="button" class="hub-side-btn hub-controls-btn" data-controls-btn>${t("CONTROLES")}</button>
        <div class="hub-lang" role="group" aria-label="${t("Idioma")}">
          <button type="button" class="hub-lang-btn" data-lang="es" aria-pressed="${lang() === "es"}">ES</button>
          <button type="button" class="hub-lang-btn" data-lang="en" aria-pressed="${lang() === "en"}">EN</button>
        </div>
      </div>
      </div>

      ${renderDonationFooter()}
      <nav class="hub-legal" aria-label="${t("Información legal")}">
        <a href="/privacy.html" target="_blank" rel="noopener">${t("Privacidad")}</a>
        <span aria-hidden="true">·</span>
        <a href="/terms.html" target="_blank" rel="noopener">${t("Términos")}</a>
      </nav>
    </div>
  `;

  root.appendChild(overlay);

  const musicBtn = overlay.querySelector<HTMLButtonElement>("[data-music]")!;

  const controlsList = overlay.querySelector<HTMLElement>("[data-controls-list]")!;
  const syncControls = (): void => {
    controlsList.innerHTML = controlsStripHtml();
  };
  const unsubControls = onControlsChange(syncControls);
  // Los nombres de tecla de TU teclado (AZERTY: la Z) llegan despues.
  void primeKeyLabels();
  /** Con CONTROLES abierto, las letras son para asignar, no atajos del menu. */
  let controlsOpen = false;
  overlay.querySelector<HTMLButtonElement>("[data-controls-btn]")!.addEventListener("click", (event) => {
    event.stopPropagation();
    if (controlsOpen) return;
    controlsOpen = true;
    // En tactil no hay teclas que cambiar, pero si un pulgar: TU MANDO.
    void (wantsTouchControls() ? openTouchEditor() : openControlsDialog()).then(() => {
      controlsOpen = false;
      (overlay.querySelector("[data-controls-btn]") as HTMLButtonElement | null)?.focus({ preventScroll: true });
    });
  });

  // Cambiar de idioma recarga la pagina (ver i18n/index.ts): la interfaz se
  // construye con plantillas ya resueltas, asi que re-traducirla en caliente
  // obligaria a re-renderizar cada pantalla a mano.
  for (const btn of overlay.querySelectorAll<HTMLButtonElement>("[data-lang]")) {
    btn.addEventListener("click", (event) => {
      event.stopPropagation();
      // Si de verdad cambia, se cede el control al bucle para que reconstruya
      // los menus con el idioma nuevo. Antes esto recargaba la pagina entera.
      if (setLang(btn.dataset.lang as Lang)) confirm("relang");
    });
  }

  const identityName = overlay.querySelector<HTMLElement>("[data-identity-name]")!;
  const syncIdentity = (): void => {
    const nick = loadNickname();
    identityName.textContent = nick ? `@${nick}` : t("Elige tu apodo");
  };
  syncIdentity();
  const unsubNick = onNicknameChange(syncIdentity);
  overlay.querySelector<HTMLButtonElement>("[data-identity]")!.addEventListener("click", (event) => {
    event.stopPropagation();
    void editNickname();
  });

  const careerBtn = overlay.querySelector<HTMLButtonElement>("[data-career]")!;
  const careerTeaser = overlay.querySelector<HTMLElement>("[data-career-teaser]")!;

  const worldTeaser = overlay.querySelector<HTMLElement>("[data-world-teaser]")!;
  const syncCareerBtn = (): void => {
    const teaser = options.careerTeaser?.() ?? "";
    careerTeaser.textContent = teaser;
    careerTeaser.classList.toggle("is-empty", teaser === "");
    worldTeaser.textContent = options.worldTeaser?.() || t("Compite contra el mundo");
  };

  careerBtn.addEventListener("click", (event) => {
    event.stopPropagation();
    confirm("career");
  });
  const syncMusicBtn = (): void => {
    const on = options.music?.isEnabled() ?? true;
    musicBtn.textContent = musicLabel(on);
    musicBtn.setAttribute("aria-pressed", on ? "true" : "false");
    musicBtn.classList.toggle("is-off", !on);
  };
  syncMusicBtn();

  musicBtn.addEventListener("click", (event) => {
    event.stopPropagation();
    const music = options.music;
    if (!music) return;
    music.setEnabled(!music.isEnabled());
    syncMusicBtn();
  });

  // --- amigos ---
  const friendsBtn = overlay.querySelector<HTMLButtonElement>("[data-friends]")!;
  const friendsCount = overlay.querySelector<HTMLElement>("[data-friends-count]")!;
  const friendsLabel = overlay.querySelector<HTMLElement>("[data-friends-label]")!;
  const squad = overlay.querySelector<HTMLElement>("[data-squad]")!;
  const friendsBadge = overlay.querySelector<HTMLElement>("[data-friends-badge]")!;
  const roomsDesc = overlay.querySelector<HTMLElement>("[data-rooms-desc]")!;
  const roomsDescDefault = roomsDesc.textContent ?? "";
  const syncSocial = (): void => {
    const social = options.social;
    if (!social) return;
    const sum = social.summary();
    friendsBtn.hidden = false;
    const party = sum.party;
    friendsLabel.textContent = party ? t("GRUPO") : t("AMIGOS");
    squad.hidden = !party;
    friendsBtn.classList.toggle("has-party", Boolean(party));
    if (party) {
      // Tu cara primero (con corona si mandas) y hasta tres mas; el resto, "+n".
      const me = loadNickname() || t("Tú");
      const faces = [
        { nickname: me, lead: party.leader, away: false },
        ...party.mates.map((m) => ({ nickname: m.nickname, lead: m.clientId === party.leaderId, away: m.status === "away" })),
      ];
      const shown = faces.slice(0, 4);
      squad.replaceChildren(
        ...shown.map((f) => {
          const i = document.createElement("i");
          i.className = `hub-squad-face${f.lead ? " is-lead" : ""}${f.away ? " is-away" : ""}`;
          i.textContent = (f.nickname.trim()[0] ?? "?").toUpperCase();
          i.style.setProperty("--face", `hsl(${[...f.nickname].reduce((h, c) => (h * 31 + c.charCodeAt(0)) % 360, 7)} 55% 42%)`);
          i.title = f.nickname;
          return i;
        }),
      );
      if (faces.length > shown.length) {
        const more = document.createElement("i");
        more.className = "hub-squad-more";
        more.textContent = `+${faces.length - shown.length}`;
        squad.appendChild(more);
      }
      friendsCount.textContent = `${faces.length}/${party.max}`;
    } else {
      friendsCount.textContent = sum.online > 0 ? `● ${sum.online}` : "";
    }
    friendsBtn.classList.toggle("has-online", !party && sum.online > 0);
    friendsBadge.hidden = sum.pending === 0;
    friendsBadge.textContent = sum.pending > 9 ? "9+" : String(sum.pending);
    friendsBtn.setAttribute(
      "aria-label",
      party
        ? t("Tu grupo: {names}", { names: party.mates.map((m) => m.nickname).join(", ") })
        : sum.online > 0
          ? t("Amigos: {n} conectados", { n: sum.online })
          : t("Amigos"),
    );
    roomsDesc.textContent = sum.line || roomsDescDefault;
    roomsDesc.classList.toggle("is-live", sum.line !== "");
  };
  const unsubSocial = options.social?.subscribe(syncSocial) ?? null;
  syncSocial();
  friendsBtn.addEventListener("click", (event) => {
    event.stopPropagation();
    options.social?.open();
  });

  let teardownAmbience: (() => void) | null = null;
  let resolvePick: ((c: MainMenuChoice) => void) | null = null;

  const hide = (): void => {
    overlay.classList.add("is-hidden");
    teardownAmbience?.();
    teardownAmbience = null;
  };

  const confirm = (choice: MainMenuChoice): void => {
    if (!resolvePick) return;
    const done = resolvePick;
    resolvePick = null;
    hide();
    done(choice);
  };

  for (const btn of overlay.querySelectorAll<HTMLButtonElement>(".hub-card")) {
    btn.addEventListener("click", () => {
      const choice = btn.dataset.choice as MainMenuChoice;
      confirm(choice);
    });
  }

  /**
   * Navegacion con flechas, como un menu de consola.
   *
   * La seleccion NO se lleva aparte del foco del navegador: mover con las
   * flechas mueve el foco, y asi el Enter lo resuelve el propio boton. Un
   * estado de seleccion paralelo al foco es la forma clasica de acabar con el
   * teclado marcando una opcion y el raton otra distinta.
   */
  const cards = [...overlay.querySelectorAll<HTMLButtonElement>(".hub-card")];
  let selected = 0;

  const applySelection = (next: number, moveFocus = true): void => {
    if (cards.length === 0) return;
    selected = (next + cards.length) % cards.length;
    cards.forEach((card, i) => card.classList.toggle("is-selected", i === selected));
    if (moveFocus) cards[selected]?.focus();
  };

  cards.forEach((card, i) => {
    card.addEventListener("focus", () => applySelection(i, false));
    card.addEventListener("mouseenter", () => applySelection(i, false));
  });
  applySelection(0, false);

  const onKey = (event: KeyboardEvent): void => {
    if (overlay.classList.contains("is-hidden")) return;
    // Con el panel de amigos o los controles abiertos, las teclas son suyas.
    if (options.social?.panelOpen() || controlsOpen) return;
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      applySelection(selected + (event.key === "ArrowDown" ? 1 : -1));
    } else if (event.key === "Enter") {
      // Con una tarjeta enfocada el navegador ya dispara su click: entrar aqui
      // tambien confirmaria dos veces.
      const active = document.activeElement;
      if (active instanceof HTMLElement && active.classList.contains("hub-card")) return;
      event.preventDefault();
      cards[selected]?.click();
    } else if (event.key === "o" || event.key === "O") {
      event.preventDefault();
      confirm("online");
    } else if (event.key === "p" || event.key === "P") {
      event.preventDefault();
      confirm("private");
    } else if (event.key === "m" || event.key === "M") {
      event.preventDefault();
      musicBtn.click();
    } else if (event.key === "c" || event.key === "C") {
      event.preventDefault();
      confirm("career");
    }
  };
  window.addEventListener("keydown", onKey);

  hide();

  return {
    show(): Promise<MainMenuChoice> {
      overlay.classList.remove("is-hidden");
      syncMusicBtn();
      syncCareerBtn();
      syncSocial();
      teardownAmbience = bindMenuAmbience(overlay);
      (overlay.querySelector('[data-choice="online"]') as HTMLButtonElement)?.focus();
      return new Promise((resolve) => {
        resolvePick = resolve;
      });
    },
    hide,
    leave: () => confirm("again"),
    dispose() {
      teardownAmbience?.();
      window.removeEventListener("keydown", onKey);
      unsubControls();
      unsubNick();
      unsubSocial?.();
      overlay.remove();
    },
  };
}
